import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  orderBy,
  setDoc
} from 'firebase/firestore';
import { db } from '../config';
import { PricingRule, SpecialPricing, MarinaSetting } from '../../models/types';
import { Timestamp } from 'firebase/firestore';
import { getMarinaSettings, updateMarinaSettings } from './marinaSettingsService';

// Settings document references
const SETTINGS_DOC_ID = 'marinaSettings';
const PRICING_RULES_DOC_ID = 'pricingRules';
const settingsRef = collection(db, 'settings');
const pricingRulesRef = doc(db, 'settings', PRICING_RULES_DOC_ID);

// Legacy collection references (kept for migration)
const PRICING_RULES_COLLECTION = 'pricingRules';
const SPECIAL_PRICING_COLLECTION = 'specialPricing';

// Standard Pricing Rules
export const getPricingRules = async (): Promise<PricingRule[]> => {
  try {
    // Get pricing rules from the settings/pricingRules document
    const docSnap = await getDoc(pricingRulesRef);
    
    if (docSnap.exists()) {
      // Return the rules array from the document
      return docSnap.data().rules || [];
    }
    
    // If document doesn't exist yet, return empty array
    return [];
  } catch (error) {
    console.error('Error getting pricing rules:', error);
    throw error;
  }
};

export const addPricingRule = async (rule: Omit<PricingRule, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    // Get existing pricing rules
    const existingRules = await getPricingRules();
    
    // Create a new rule with ID and timestamps
    const now = new Date().toISOString();
    const newRule: PricingRule = {
      ...rule,
      id: Date.now().toString(), // Generate a timestamp-based ID
      createdAt: now,
      updatedAt: now
    };
    
    // Add the new rule to existing pricing rules
    const updatedRules = [...existingRules, newRule];
    
    // Update the pricingRules document with the new rules array
    await setDoc(pricingRulesRef, { rules: updatedRules }, { merge: true });
    
    return newRule.id;
  } catch (error) {
    console.error('Error adding pricing rule:', error);
    throw error;
  }
};

export const updatePricingRule = async (id: string, rule: Partial<PricingRule>): Promise<void> => {
  try {
    // Get existing pricing rules
    const existingRules = await getPricingRules();
    
    // Find the rule to update
    const ruleIndex = existingRules.findIndex(r => r.id === id);
    
    if (ruleIndex === -1) {
      throw new Error(`Pricing rule with ID ${id} not found`);
    }
    
    // Update the rule
    const updatedRule = {
      ...existingRules[ruleIndex],
      ...rule,
      updatedAt: new Date().toISOString()
    };
    
    // Replace the old rule with the updated one
    const updatedRules = [...existingRules];
    updatedRules[ruleIndex] = updatedRule;
    
    // Update the pricingRules document with the updated rules array
    await setDoc(pricingRulesRef, { rules: updatedRules }, { merge: true });
  } catch (error) {
    console.error('Error updating pricing rule:', error);
    throw error;
  }
};

export const deletePricingRule = async (id: string): Promise<void> => {
  try {
    // Get existing pricing rules
    const existingRules = await getPricingRules();
    
    // Filter out the rule to delete
    const updatedRules = existingRules.filter(rule => rule.id !== id);
    
    // Update the pricingRules document with the filtered rules
    await setDoc(pricingRulesRef, { rules: updatedRules }, { merge: true });
  } catch (error) {
    console.error('Error deleting pricing rule:', error);
    throw error;
  }
};

// Special Pricing Rules
export const getSpecialPricing = async (): Promise<SpecialPricing[]> => {
  const querySnapshot = await getDocs(collection(db, SPECIAL_PRICING_COLLECTION));
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as SpecialPricing));
};

// Helper function to get active pricing for a vessel type
export const getActivePricingForVesselType = async (vesselType: string): Promise<PricingRule | null> => {
  try {
    // Get all pricing rules
    const allRules = await getPricingRules();
    
    // Find the active rule for this vessel type
    const activeRule = allRules.find(rule => 
      rule.vesselType === vesselType && 
      rule.id // Using presence of ID to indicate active status
    );
    
    return activeRule || null;
  } catch (error) {
    console.error('Error getting active pricing for vessel type:', error);
    throw error;
  }
};

// Get pricing rules by vessel type
export const getPricingRulesByVesselType = async (vesselType: string): Promise<PricingRule[]> => {
  try {
    // Get all pricing rules
    const allRules = await getPricingRules();
    
    // Filter rules by vessel type and active status
    return allRules.filter(rule => 
      rule.vesselType === vesselType && 
      rule.id // Using presence of ID to indicate active status
    );
  } catch (error) {
    console.error('Error getting pricing rules by vessel type:', error);
    throw error;
  }
};

// Get pricing rules by vessel ID
export const getPricingRulesByVesselId = async (vesselId: string): Promise<PricingRule[]> => {
  try {
    // Get all pricing rules
    const allRules = await getPricingRules();
    
    // Filter rules by vessel ID and active status
    return allRules.filter(rule => 
      rule.vesselId === vesselId && 
      rule.id // Using presence of ID to indicate active status
    );
  } catch (error) {
    console.error('Error getting pricing rules by vessel ID:', error);
    throw error;
  }
};

// Calculate price for a reservation
export const calculatePrice = async (
  vesselType: string,
  vesselId: string,
  date: string,
  duration: number
): Promise<{ basePrice: number; timeBlockPrice: number; totalPrice: number }> => {
  try {
    // Get all applicable pricing rules
    const [vesselTypeRules, vesselSpecificRules] = await Promise.all([
      getPricingRulesByVesselType(vesselType),
      getPricingRulesByVesselId(vesselId)
    ]);

    // Combine and sort rules by specificity (vessel-specific rules take precedence)
    const allRules = [...vesselTypeRules, ...vesselSpecificRules];
    
    // Find the most specific applicable rule
    const applicableRule = allRules.find(rule => {
      const isDateInRange = (!rule.startDate || date >= rule.startDate) && 
                           (!rule.endDate || date <= rule.endDate);
      
      const isDayApplicable = !rule.daysOfWeek || 
                             rule.daysOfWeek.includes(new Date(date).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase());
      
      return isDateInRange && isDayApplicable;
    });

    if (!applicableRule) {
      throw new Error('No applicable pricing rule found');
    }

    // Find the appropriate time block price
    const timeBlock = applicableRule.timeBlockPrices
      .sort((a, b) => b.hours - a.hours) // Sort in descending order
      .find(block => duration >= block.hours);

    if (!timeBlock) {
      throw new Error('No applicable time block found for the given duration');
    }

    const totalPrice = applicableRule.basePrice + timeBlock.price;

    return {
      basePrice: applicableRule.basePrice,
      timeBlockPrice: timeBlock.price,
      totalPrice
    };
  } catch (error) {
    console.error('Error calculating price:', error);
    throw error;
  }
};

// Get vessel pricing configuration for duration options
export const getVesselPricingOptions = async (vesselType: string): Promise<{
  durationOptions: Array<{ hours: number; price: number; description?: string }>;
  pricingModel: string;
  baseRate?: number;
}> => {
  try {
    // First try to get from PricingRules (newer system)
    const pricingRules = await getPricingRulesByVesselType(vesselType);
    
    if (pricingRules.length > 0) {
      // Use the first active rule for this vessel type
      const activeRule = pricingRules[0];
      
      // Return time block prices as duration options
      const durationOptions = activeRule.timeBlockPrices
        .sort((a, b) => a.hours - b.hours) // Sort by hours ascending
        .map(timeBlock => ({
          hours: timeBlock.hours,
          price: timeBlock.price,
          description: getHourDescription(timeBlock.hours)
        }));
      
      return {
        durationOptions,
        pricingModel: 'timeBlock',
        baseRate: activeRule.basePrice
      };
    }
    
    // Fallback to vessel pricing settings (legacy system)
    const vesselPricingDoc = await getDoc(doc(db, 'settings', 'vesselPricing'));
    
    if (vesselPricingDoc.exists()) {
      const vesselPricing = vesselPricingDoc.data();
      const vesselPrice = vesselPricing[vesselType];
      
      if (vesselPrice) {
        if (vesselPrice.model === 'multiHour' && vesselPrice.discounts) {
          // Convert multiHour discounts to duration options
          const baseRate = vesselPrice.baseRate || 100;
          
          const durationOptions = vesselPrice.discounts
            .sort((a: any, b: any) => a.hours - b.hours)
            .map((discount: any) => {
              const price = baseRate * discount.hours * (1 - discount.discount / 100);
              return {
                hours: discount.hours,
                price: Math.round(price * 100) / 100, // Round to 2 decimal places
                description: getHourDescription(discount.hours)
              };
            });
          
          return {
            durationOptions,
            pricingModel: 'multiHour',
            baseRate
          };
        } else if (vesselPrice.model === 'hourly') {
          // For hourly pricing, create common duration options
          const baseRate = vesselPrice.rate || 100;
          const commonDurations = [1, 2, 3, 4, 5, 6, 7, 8];
          
          const durationOptions = commonDurations.map(hours => ({
            hours,
            price: baseRate * hours,
            description: getHourDescription(hours)
          }));
          
          return {
            durationOptions,
            pricingModel: 'hourly',
            baseRate
          };
        }
      }
    }
    
    // Default fallback - create standard duration options
    const defaultRate = 100; // Default hourly rate
    const commonDurations = [3, 7]; // Default durations
    
    const durationOptions = commonDurations.map(hours => ({
      hours,
      price: defaultRate * hours,
      description: getHourDescription(hours)
    }));
    
    return {
      durationOptions,
      pricingModel: 'default',
      baseRate: defaultRate
    };
    
  } catch (error) {
    console.error('Error getting vessel pricing options:', error);
    
    // Return default options on error
    const defaultRate = 100;
    const commonDurations = [3, 7];
    
    const durationOptions = commonDurations.map(hours => ({
      hours,
      price: defaultRate * hours,
      description: getHourDescription(hours)
    }));
    
    return {
      durationOptions,
      pricingModel: 'default',
      baseRate: defaultRate
    };
  }
};

// Helper function to get descriptive text for duration
const getHourDescription = (hours: number): string => {
  if (hours === 1) return 'Quick adventure';
  if (hours <= 2) return 'Short trip';
  if (hours <= 4) return 'Half-day adventure';
  if (hours <= 6) return 'Extended outing';
  if (hours <= 8) return 'Full-day experience';
  return 'Extended rental';
}; 